import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createStudentAPI,
  createLevelAPI,
  fetchLevelListAPI,
  fetchStudentsAPI,
  updateLevelAPI,
  deleteStudentLevelAPI,
  updateStudentAPI,
  deleteStudentAPI,
  deleteStudentListAPI,
  createStudentLevelAPI,
  deleteLevelAPI,
  createBulkStudentAPI,
  bulkIncreaseStudentGradeAPI,
} from "./studentAPI";
import { showClass } from "../../utils/textUtils";

const initialState = {
  levels: [],
  allStudents: [],
  filteredStudents: [],
  loading: false,
  error: null,
};

export const updateLevel = createAsyncThunk(
  "students/updateLevel",
  async ({ levelData, id }, { rejectWithValue }) => {
    try {
      const data = await updateLevelAPI(levelData, id);
      return data; // 업데이트된 level 정보
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// 1) 반 리스트 조회
export const fetchLevels = createAsyncThunk(
  "students/fetchLevels",
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchLevelListAPI();
      return data; // 배열 형태 또는 {id, name}[]
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const { levels } = getState().student;
      if (levels.length > 0) {
        return false;
      }
    },
  }
);

export const fetchStudents = createAsyncThunk(
  "students/fetchStudents",
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchStudentsAPI();
      return data; // 배열 형태 또는 {id, name}[]
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// 2) 학생 생성
export const createStudent = createAsyncThunk(
  "students/createStudent",
  async (studentData, { rejectWithValue }) => {
    try {
      const data = await createStudentAPI(studentData);
      return data; // 생성된 student 정보
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// 3) 학생 대량 생성
export const createBulkStudent = createAsyncThunk(
  "students/createBulkStudent",
  async (studentData, { rejectWithValue }) => {
    try {
      const data = await createBulkStudentAPI(studentData);
      return data; // 생성된 student 정보
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// 3) 반 생성
export const createLevel = createAsyncThunk(
  "students/createLevel",
  async (levelData, { rejectWithValue }) => {
    try {
      const data = await createLevelAPI(levelData);
      return data; // 생성된 level 정보
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateStudent = createAsyncThunk(
  "students/updateStudent",
  async ({ studentData, id }, { rejectWithValue }) => {
    try {
      const data = await updateStudentAPI(studentData, id);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteStudentLevel = createAsyncThunk(
  "students/deleteStudentLevel",
  async ({ studentId, levelId }, { rejectWithValue }) => {
    try {
      const data = await deleteStudentLevelAPI(studentId, levelId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteStudent = createAsyncThunk(
  "students/deleteStudent",
  async ({ studentId }, { rejectWithValue }) => {
    try {
      const data = await deleteStudentAPI(studentId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteLevel = createAsyncThunk(
  "students/deleteLevel",
  async ({ levelId }, { rejectWithValue }) => {
    try {
      const data = await deleteLevelAPI(levelId);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// 여러 명을 지울 때는 요청 하나로 보낸다. 예전에는 학생 수만큼 DELETE 를 동시에
// 던졌는데, 토큰이 만료돼 있으면 그 요청들이 각자 갱신을 부르다 서로를 밀어내
// 로그아웃되고 한 명도 안 지워졌다 (apiClient.js 의 refreshAccessToken 참고).
export const deleteStudentList = createAsyncThunk(
  "students/deleteStudentList",
  async ({ studentIds }, { rejectWithValue }) => {
    try {
      const data = await deleteStudentListAPI(studentIds);
      return {
        successfulIds: data.deleted_id_list || [],
        missingIds: data.missing_id_list || [],
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createStudentLevelList = createAsyncThunk(
  "students/createStudentLevelList",
  async ({ studentIdList, levelId }, { rejectWithValue }) => {
    const successfulIds = [];
    const failedIds = [];

    await Promise.all(
      studentIdList.map(async (studentId) => {
        try {
          await createStudentLevelAPI(studentId, levelId);
          successfulIds.push({
            studentId: studentId,
            levelId: levelId,
          });
        } catch (error) {
          failedIds.push({
            studentId: studentId,
            levelId: levelId,
            error: error.response?.data || error.message,
          });
        }
      })
    );

    return { successfulIds, failedIds };
  }
);

export const bulkIncreaseStudentGrade = createAsyncThunk(
  "students/bulkIncreaseGrade",
  async (_, { rejectWithValue }) => {
    try {
      const data = await bulkIncreaseStudentGradeAPI();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const studentSlice = createSlice({
  name: "students",
  initialState,
  reducers: {
    sortStudents: (state, action) => {
      const sortMethod = action.payload.toLowerCase();
      if (sortMethod === "name") {
        state.filteredStudents = state.filteredStudents.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
      } else if (sortMethod === "grade") {
        state.filteredStudents = state.filteredStudents.sort(
          (a, b) => a.grade - b.grade
        );
      } else if (sortMethod === "level") {
        state.filteredStudents = state.filteredStudents.sort((a, b) => {
          if (a.level_list.length === 0) {
            return 1;
          } else if (b.level_list.length === 0) {
            return -1;
          }
          return showClass(a.level_list).localeCompare(showClass(b.level_list));
        });
      } else {
        state.filteredStudents = state.filteredStudents.sort(
          (a, b) => a.id - b.id
        );
      }
    },
    filterStudentsByKeyword: (state, action) => {
      const keyword = action.payload.toLowerCase();
      if (keyword.trim() === "") {
        state.filteredStudents = state.allStudents;
      } else {
        state.filteredStudents = state.allStudents.filter((student) =>
          student.name.toLowerCase().includes(keyword)
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchLevels
      .addCase(fetchLevels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLevels.fulfilled, (state, action) => {
        state.loading = false;
        state.levels = action.payload;
      })
      .addCase(fetchLevels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // fetchStudents
      .addCase(fetchStudents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudents.fulfilled, (state, action) => {
        state.loading = false;
        state.allStudents = action.payload;
        state.filteredStudents = action.payload;
      })

      // createStudent
      .addCase(createStudent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createStudent.fulfilled, (state, action) => {
        state.loading = false;
        state.allStudents.push({
          ...action.payload,
          level_list: [],
        });
        state.filteredStudents.push({
          ...action.payload,
          level_list: [],
        });
      })
      .addCase(createStudent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // createBulkStudent
      .addCase(createBulkStudent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createBulkStudent.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(createBulkStudent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // createLevel
      .addCase(createLevel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLevel.fulfilled, (state, action) => {
        state.loading = false;
        // 새로 생성된 level을 levels 배열에 추가 (옵션)
        state.levels.push(action.payload);
      })
      .addCase(createLevel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // updateLevel
      .addCase(updateLevel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateLevel.fulfilled, (state, action) => {
        state.loading = false;
        // 업데이트된 level 정보로 levels 배열 업데이트
        state.levels = state.levels.map((level) =>
          level.id === action.payload.id ? action.payload : level
        );
      })
      .addCase(updateLevel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // updateStudent
      .addCase(updateStudent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStudent.fulfilled, (state, action) => {
        state.loading = false;
        state.allStudents = state.allStudents.map((student) =>
          student.id === action.payload.id
            ? {
                ...student,
                ...action.payload,
              }
            : student
        );
        state.filteredStudents = state.filteredStudents.map((student) =>
          student.id === action.payload.id
            ? {
                ...student,
                ...action.payload,
              }
            : student
        );
      })
      .addCase(updateStudent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // deleteStudentLevel
      .addCase(deleteStudentLevel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteStudentLevel.fulfilled, (state, action) => {
        state.loading = false;

        const { student, level } = action.payload;
        state.allStudents = state.allStudents.map((s) =>
          s.id === student.id
            ? {
                ...s,
                level_list: s.level_list.filter(
                  (tmp_level) => tmp_level.id !== level.id
                ),
              }
            : s
        );
        state.filteredStudents = state.filteredStudents.map((s) =>
          s.id === student.id
            ? {
                ...s,
                level_list: s.level_list.filter(
                  (tmp_level) => tmp_level.id !== level.id
                ),
              }
            : s
        );
      })
      .addCase(deleteStudentLevel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // deleteStudent
      .addCase(deleteStudent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteStudent.fulfilled, (state, action) => {
        state.loading = false;
        state.allStudents = state.allStudents.filter(
          (student) => student.id !== action.payload.id
        );
        state.filteredStudents = state.filteredStudents.filter(
          (student) => student.id !== action.payload.id
        );
      })
      .addCase(deleteStudent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteStudentList.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteStudentList.fulfilled, (state, action) => {
        state.loading = false;

        state.allStudents = state.allStudents.filter(
          (student) => !action.payload.successfulIds.includes(student.id)
        );
        state.filteredStudents = state.filteredStudents.filter(
          (student) => !action.payload.successfulIds.includes(student.id)
        );

        state.error = null;
      })
      .addCase(deleteStudentList.rejected, (state, action) => {
        state.loading = false;
        // state.error 는 배열이 아니라 push 하면 그 자리에서 터진다. 실패를 알리는
        // 자리에서 다시 실패하면 무엇이 잘못됐는지 알 수 없게 된다.
        state.error = action.payload || "학생 삭제에 실패했습니다.";
      })

      .addCase(createStudentLevelList.pending, (state) => {
        state.loading = true;
      })
      .addCase(createStudentLevelList.fulfilled, (state, action) => {
        state.loading = false;

        action.payload.successfulIds.forEach((data) => {
          state.allStudents = state.allStudents.map((student) =>
            student.id === data.studentId
              ? {
                  ...student,
                  level_list: [
                    ...student.level_list,
                    state.levels.find(
                      (level) => level.id === parseInt(data.levelId, 10)
                    ),
                  ],
                }
              : student
          );
          state.filteredStudents = state.filteredStudents.map((student) =>
            student.id === data.studentId
              ? {
                  ...student,
                  level_list: [
                    ...student.level_list,
                    state.levels.find(
                      (level) => level.id === parseInt(data.levelId, 10)
                    ),
                  ],
                }
              : student
          );
        });

        state.error = action.payload.failedIds;
      })
      .addCase(createStudentLevelList.rejected, (state, action) => {
        state.loading = false;
        state.error.push(action.payload || "Unknown error occurred");
      })
      .addCase(deleteLevel.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteLevel.fulfilled, (state, action) => {
        state.loading = false;
        state.levels = state.levels.filter(
          (level) => level.id !== action.payload.id
        );
      })
      .addCase(deleteLevel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // bulkIncreaseStudentGrade
      .addCase(bulkIncreaseStudentGrade.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkIncreaseStudentGrade.fulfilled, (state) => {
        state.loading = false;
        // Increment grade for all students with grade < 12
        state.allStudents = state.allStudents.map((student) =>
          student.grade < 12
            ? { ...student, grade: student.grade + 1 }
            : student
        );
        state.filteredStudents = state.filteredStudents.map((student) =>
          student.grade < 12
            ? { ...student, grade: student.grade + 1 }
            : student
        );
      })
      .addCase(bulkIncreaseStudentGrade.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { filterStudentsByKeyword, sortStudents } = studentSlice.actions;
export default studentSlice.reducer;
